import React from 'react';
import {
  FiEdit2,
  FiMoreVertical,
  FiPause,
  FiPlay,
  FiStopCircle,
  FiTrash2,
  FiZap,
} from 'react-icons/fi';

import { Button } from '../../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../ui/dropdown-menu';
import IconWrapper from '../../../ui/IconWrapper';
import type { ScheduledJob } from '../../../../types/scheduledJobs';

interface ScheduledJobActionsProps {
  job: ScheduledJob;
  onRunNow: (job: ScheduledJob) => void;
  onPause: (job: ScheduledJob) => void;
  onResume: (job: ScheduledJob) => void;
  onStop: (job: ScheduledJob) => void;
  onEdit: (job: ScheduledJob) => void;
  onDelete: (job: ScheduledJob) => void;
  disabled?: boolean;
}

/**
 * Action menu for a single scheduled job row.
 *
 * - `Stop` only renders when `latest_run.status === 'running'`.
 * - `Pause` / `Resume` are rendered based on `job.status`.
 */
const ScheduledJobActions: React.FC<ScheduledJobActionsProps> = ({
  job,
  onRunNow,
  onPause,
  onResume,
  onStop,
  onEdit,
  onDelete,
  disabled = false,
}) => {
  const isRunning = job.latest_run?.status === 'running';
  const isPaused = job.status === 'paused';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={`Actions for ${job.name}`}
          disabled={disabled}
          data-testid="scheduled-job-actions-trigger"
        >
          <IconWrapper icon={FiMoreVertical} className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuItem
          onSelect={(event) => {
            event.preventDefault();
            onRunNow(job);
          }}
          data-testid="scheduled-job-action-run-now"
        >
          <IconWrapper icon={FiZap} className="mr-2 h-4 w-4" />
          Run now
        </DropdownMenuItem>

        {isPaused ? (
          <DropdownMenuItem
            onSelect={(event) => {
              event.preventDefault();
              onResume(job);
            }}
            data-testid="scheduled-job-action-resume"
          >
            <IconWrapper icon={FiPlay} className="mr-2 h-4 w-4" />
            Resume
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            onSelect={(event) => {
              event.preventDefault();
              onPause(job);
            }}
            data-testid="scheduled-job-action-pause"
          >
            <IconWrapper icon={FiPause} className="mr-2 h-4 w-4" />
            Pause
          </DropdownMenuItem>
        )}

        {isRunning && (
          <DropdownMenuItem
            onSelect={(event) => {
              event.preventDefault();
              onStop(job);
            }}
            data-testid="scheduled-job-action-stop"
          >
            <IconWrapper icon={FiStopCircle} className="mr-2 h-4 w-4" />
            Stop run
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onSelect={(event) => {
            event.preventDefault();
            onEdit(job);
          }}
          data-testid="scheduled-job-action-edit"
        >
          <IconWrapper icon={FiEdit2} className="mr-2 h-4 w-4" />
          Edit
        </DropdownMenuItem>

        <DropdownMenuItem
          variant="destructive"
          onSelect={(event) => {
            event.preventDefault();
            onDelete(job);
          }}
          data-testid="scheduled-job-action-delete"
        >
          <IconWrapper icon={FiTrash2} className="mr-2 h-4 w-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ScheduledJobActions;
